import { intl } from "LocaleWrapper";
import { useApolloClient } from "@apollo/client";

import { Select } from "@cofano/cck";
import { SelectProps } from "@cofano/cck/Select/Select";

import { GetProductsForSelectDocument, GetProductsForSelectQuery } from "__generated__/view/products/graphql/product.queries.generated";
import { SortDirection } from "__generated__/enums";
import { Tooltip } from "@mui/material";
import WarehouseOutlined from "@mui/icons-material/WarehouseOutlined";

type Content = NonNullable<NonNullable<GetProductsForSelectQuery["productsBySearch"]>["content"]>;
export type ProductSelectOption = NonNullable<Content[number]>

const isLinkedToSupplier = (option: ProductSelectOption) => !!option.relations?.some(relation => relation.supplier);

const ProductSelect = <Multiple extends boolean = false,>({
	supplierId,
	filterLinkedSupplier,
	...rest
}: Omit<SelectProps<ProductSelectOption, Multiple>, "fetchOptions"> & { supplierId?: number | null, filterLinkedSupplier?: boolean | false, }) => {

	const client = useApolloClient();

	const supplierFilterActive = filterLinkedSupplier && supplierId;
	const onFetchOptions = async(params: string, page: number) => {
		try  {
			const productSearch = {
				partialName: params,
				isActive: true,
				supplier: supplierId ? { supplierId, includeUnlinked: !!supplierFilterActive } : undefined,
			};

			const pagination = {
				page: page - 1,
				size: 10,
				sortList: supplierFilterActive ? [ { field: "supplier", direction: SortDirection.Asc } ] : undefined,
			};

			const result = await client.query({
				query: GetProductsForSelectDocument,
				variables: { productSearch, pagination },
			});

			const options = (result.data.productsBySearch?.content || []) as ProductSelectOption[];

			return {
				options,
				totalResults: result.data.productsBySearch?.totalElements || 0,
			};
		} catch (response) {
			return {
				options: [],
				totalResults: 0,
			};
		}
	};

	return (
		<Select
			typeToSearchLabel={intl.formatMessage({ id: "TYPE_TO_SEARCH" })}
			loadingLabel={intl.formatMessage({ id: "LOADING_DATA" })}
			fetchOptions={onFetchOptions}
			popoverFullWidth={true}
			getOptionLabel={option => option.name}
			renderOption={(props, option) => (
				<li {...props}>
					<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
						<div>
							<div>{option.name}</div>
							<div>
								<small>{option.code}</small>
							</div>
						</div>
						<div style={{ display: "flex", alignItems: "center" }}>
							{isLinkedToSupplier(option) && (
								<Tooltip title={intl.formatMessage({ id: "SUPPLIER_PRODUCT" })}>
									<WarehouseOutlined fontSize="small" />
								</Tooltip>
							)}
						</div>
					</div>
				</li>
			)}
			{...rest}
			popoverHeight={300}
			paginated />
	);
};

export default ProductSelect;
